import { OvCard, OvForm, OvFormInput } from '@components'
import { TOOLTIPS } from '@constants'
import { useStore } from '@hooks/useStore'
import { Alert, Box, Button, Card, Chip, Tooltip, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import CancelIcon from '@mui/icons-material/Cancel'
import SendIcon from '@mui/icons-material/Send'
import PendingIcon from '@mui/icons-material/Pending'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { EmailChangeForm } from './email-change-form'

export const ProfileForm = observer(() => {
  const { user, ui } = useStore()
  const [cancellingChange, setCancellingChange] = useState(false)
  const [resendingEmail, setResendingEmail] = useState(false)
  const [emailChangeModalOpen, setEmailChangeModalOpen] = useState(false)

  const handleCancelEmailChange = async () => {
    try {
      setCancellingChange(true)
      await user.cancelEmailChange()
      ui.handleToaster('E-mailadres wijzigingsverzoek geannuleerd', { type: 'success' })
    } catch (error: any) {
      ui.handleToaster(error?.response?.data?.message || 'Er is een fout opgetreden', { type: 'error' })
    } finally {
      setCancellingChange(false)
    }
  }

  const handleResendEmailChange = async () => {
    try {
      setResendingEmail(true)
      await user.resendEmailChange()
      ui.handleToaster('Verificatie e-mail opnieuw verzonden', { type: 'success' })
    } catch (error: any) {
      ui.handleToaster(error?.response?.data?.message || 'Er is een fout opgetreden', { type: 'error' })
    } finally {
      setResendingEmail(false)
    }
  }

  const FormInput = OvFormInput

  return (
    <OvForm>
      <OvCard>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <FormInput
            label="Voornaam"
            name="name"
          />
          <Box>
            <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
              <Box sx={{ flex: 1 }}>
                <FormInput
                  label="E-mail"
                  name="email"
                  disabled
                  required
                />
              </Box>
              <Tooltip title={TOOLTIPS.CHANGE_EMAIL}>
                <span>
                  <Button
                    variant="outlined"
                    onClick={() => setEmailChangeModalOpen(true)}
                    disabled={!!user.current_user.pending_email_change}
                    sx={{ height: '56px' }}>
                    E-mail wijzigen
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {user.current_user.pending_email_change && (
              <Card
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e0e0e0',
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <PendingIcon color="warning" />
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}>
                    Openstaande e-mail wijziging
                  </Typography>
                  <Chip
                    label="Wacht op verificatie"
                    size="small"
                    color="warning"
                    sx={{ ml: 'auto' }}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    my: 2,
                    p: 2,
                    backgroundColor: 'white',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                  }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block">
                      Huidig e-mailadres
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}>
                      {user.current_user.email}
                    </Typography>
                  </Box>

                  <ArrowForwardIcon color="action" />

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block">
                      Nieuw e-mailadres
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="primary">
                      {user.current_user.pending_email_change.new_email}
                    </Typography>
                  </Box>
                </Box>

                <Alert
                  severity="info"
                  sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Controleer je inbox op <strong>{user.current_user.pending_email_change.new_email}</strong> en klik
                    op de verificatielink om de wijziging te voltooien.
                  </Typography>
                </Alert>

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Tooltip title={TOOLTIPS.RESEND_EMAIL_VERIFICATION}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SendIcon />}
                        onClick={handleResendEmailChange}
                        disabled={resendingEmail || cancellingChange}>
                        Verificatie opnieuw verzenden
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={TOOLTIPS.CANCEL_EMAIL_CHANGE_REQUEST}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelEmailChange}
                        disabled={cancellingChange || resendingEmail}>
                        Verzoek annuleren
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Card>
            )}
          </Box>
          <FormInput
            label="Geboortedatum"
            name="date_of_bith"
            type="date"
            required
          />
        </Box>
      </OvCard>

      <EmailChangeForm
        open={emailChangeModalOpen}
        onClose={() => setEmailChangeModalOpen(false)}
      />
    </OvForm>
  )
})
